import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = 'shabba_auth_token';
const AUTH_USER_KEY = 'shabba_auth_user';

// Get API base URL from environment or use default
const getAuthApiBase = () => {
  // Use env when set (baked in at build time for production)
  if (import.meta.env.VITE_AUTH_API_BASE) {
    return import.meta.env.VITE_AUTH_API_BASE;
  }
  // Production same-origin: backend is at /api/auth (nginx proxies)
  if (import.meta.env.PROD) {
    return '/api/auth';
  }
  // Development fallback
  return 'http://localhost:8001';
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load token and user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_USER_KEY);

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        
        // Verify token is still valid
        verifyToken(storedToken).then((isValid) => {
          if (!isValid) {
            // Token expired or invalid, clear storage
            // console.log('Token invalid, clearing auth');
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
            setToken(null);
            setUser(null);
          } else {
            // console.log('Token valid, user authenticated');
          }
        }).catch((error) => {
          console.error('Token verification error:', error);
          // Clear on error
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_USER_KEY);
          setToken(null);
          setUser(null);
        });
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
      }
    }
    
    setLoading(false);
  }, []);

  const verifyToken = async (tokenToVerify: string): Promise<boolean> => {
    try {
      const apiBase = getAuthApiBase().replace(/\/$/, '');
      const response = await fetch(`${apiBase}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToVerify }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid && data.user) {
          setUser(data.user);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const apiBase = getAuthApiBase().replace(/\/$/, '');
      const response = await fetch(`${apiBase}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success && data.token && data.user) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.token);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      }

      const serverMessage = data.error || data.message || (response.status === 401 ? 'Invalid email or password' : 'Login failed');
      return { success: false, error: serverMessage };
    } catch (error) {
      console.error('Login error:', error);
      const message = error instanceof Error ? error.message : 'Network error. Check backend URL and CORS.';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // console.log('Logging out...');
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setToken(null);
    setUser(null);
    // Force a small delay to ensure state updates
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

